import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom"; // 🌟 Navigation ke liye
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  List,
  Chip,
} from "@mui/material";
import { keyframes } from "@mui/system";
import CloseIcon from "@mui/icons-material/Close";
import PsychologyIcon from "@mui/icons-material/Psychology";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import SendIcon from "@mui/icons-material/Send";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SensorsIcon from "@mui/icons-material/Sensors";
import PhoneIcon from "@mui/icons-material/Phone";
import StarRateIcon from "@mui/icons-material/StarRate";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import TagIcon from "@mui/icons-material/Tag";
import HistoryIcon from "@mui/icons-material/History";

// ================= OPTIMIZED ANIMATIONS =================
const modalPop = keyframes`
  0% { opacity: 0; transform: translate(-50%, -48%) scale(0.98); }
  100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
`;

const aiScanLine = keyframes`
  0% { transform: translateY(0px); opacity: 0; }
  10% { opacity: 1; }
  90% { opacity: 1; }
  100% { transform: translateY(180px); opacity: 0; }
`;

const fadeIn = keyframes`
  0% { opacity: 0; }
  100% { opacity: 1; }
`;

const AI_PHRASES = [
  "Analyzing Patient Data...",
  "Running Prediction Models...",
  "Identifying Drop-off Risks...",
];

// Human-readable labels for service_status codes returned by the history API
const SERVICE_STATUS_LABELS = {
  1: "Converted Into Service",
  2: "In Follow-up",
  3: "Dropped",
};

// ================= COMPONENT =================
const AIDropoffModal = ({
  open,
  handleClose,
  accessToken,
  API_URL,
  setOpenSnackbar,
  setSnackbarMessage,
  setSnackbarSeverity,
}) => {
  const navigate = useNavigate(); // 🌟 Navigation Initialize kiya

  const [activeStep, setActiveStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0],
  );

  const [predictions, setPredictions] = useState([]);
  const [visibleCount, setVisibleCount] = useState(20);

  const [aiReasons, setAiReasons] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);

  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState(AI_PHRASES[0]);

  // 🌟 Previous follow-up history (before drop-off) state
  const [prevFollowups, setPrevFollowups] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const [aiForm, setAiForm] = useState({
    service_status: "",
    reason: "",
    Remark: "",
    in_folloup: "",
  });

  // AI Loading Animation Text
  useEffect(() => {
    let interval;
    if (loading) {
      let i = 0;
      interval = setInterval(() => {
        i = (i + 1) % AI_PHRASES.length;
        setLoadingText(AI_PHRASES[i]);
      }, 800);
    } else {
      setLoadingText(AI_PHRASES[0]);
    }
    return () => clearInterval(interval);
  }, [loading]);

  useEffect(() => {
    if (open) {
      fetchReasons();
      fetchPredictions(selectedDate);
      setActiveStep(1);
    }
  }, [open, selectedDate]);

  const fetchReasons = async () => {
    try {
      const res = await fetch(`${API_URL}/web/service_drop_reasons/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const data = await res.json();
      setAiReasons(data || []);
    } catch (error) {
      console.error("Error fetching reasons:", error);
    }
  };

  const fetchPredictions = async (dateStr) => {
    setLoading(true);
    setPredictions([]);
    setVisibleCount(20);
    try {
      const res = await fetch(`${API_URL}/web/predict-dropoff/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ date: dateStr }),
      });
      const responseData = await res.json();

      if (
        responseData.status === "success" &&
        responseData.data &&
        Array.isArray(responseData.data.patients)
      ) {
        setTimeout(() => {
          setPredictions(responseData.data.patients);
          setLoading(false);
        }, 800);
      } else {
        setTimeout(() => {
          setPredictions([]);
          setLoading(false);
        }, 500);
      }
    } catch (error) {
      console.error("Error fetching predictions:", error);
      setPredictions([]);
      setLoading(false);
    }
  };

  // 🌟 Fetch previous follow-up history for a patient (pre drop-off)
  const fetchPreviousFollowups = async (patientId) => {
    setLoadingHistory(true);
    setPrevFollowups([]);
    try {
      const res = await fetch(
        `${API_URL}/web/last_folloup_dtls_pre_dropoff/${patientId}/`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );
      const data = await res.json();
      setPrevFollowups(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching previous follow-up history:", error);
      setPrevFollowups([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleScroll = (event) => {
    const { scrollTop, clientHeight, scrollHeight } = event.target;
    if (scrollHeight - scrollTop <= clientHeight + 100) {
      if (visibleCount < predictions.length) {
        setVisibleCount((prev) => prev + 20);
      }
    }
  };

const handleAiSubmit = async (event) => {
  event.preventDefault();
  const currentStatus = parseInt(aiForm.service_status);

  // 🌟 1. Base payload (reason aur in_folloup dono isme nahi hain)
  const requestData = {
    ptn_id: parseInt(selectedPatient.patient_id),
    dtl_id: parseInt(selectedPatient.last_session_id_id),
    Remark: aiForm.Remark,
    service_status: currentStatus,
  };

  // 🌟 2. Conditions ke hisaab se keys attach karo
  if (currentStatus === 2) {
    // Sirf Follow-up ke time date bhejni hai
    requestData.in_folloup = aiForm.in_folloup;
  } else if (currentStatus === 3) {
    // Sirf Drop ke time reason key bhejni hai
    requestData.reason = parseInt(aiForm.reason);
  }

  try {
    const response = await fetch(`${API_URL}/web/submit_dropoff_reason/`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

    const responseData = await response.json();

    setOpenSnackbar(true);
    setSnackbarSeverity("success");

    if (currentStatus === 1) {
      setSnackbarMessage(
        "Patient Converted Successfully! Redirecting to Add Service...",
      );
    } else if (currentStatus === 2) {
      setSnackbarMessage("Follow-up Schedule Saved Successfully!");
    } else if (currentStatus === 3) {
      setSnackbarMessage("Patient Drop-off Recorded Successfully!");
    } else {
      setSnackbarMessage("Feedback Submitted Successfully!");
    }

    setAiForm({
      service_status: "",
      reason: "",
      Remark: "",
      in_folloup: "",
    });

    if (currentStatus === 1) {
      handleClose();
      const evId = responseData.eve_id;
      navigate("/addservice", {
        state: { eventID: evId },
      });
      return;
    }

    setSelectedPatient(null);
    setActiveStep(1);
    setPrevFollowups([]);
    fetchPredictions(selectedDate);
  } catch (error) {
    setOpenSnackbar(true);
    setSnackbarMessage("Failed to submit details. Please try again.");
    setSnackbarSeverity("error");
  }
};

  const handleDateChange = (e) => setSelectedDate(e.target.value);

  const handleActionClick = (patient) => {
    setSelectedPatient(patient);
    setActiveStep(2);
    fetchPreviousFollowups(patient.patient_id); // 🌟 load history for this patient
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setAiForm((prev) => ({ ...prev, [name]: value }));
  };

  const closeAndReset = () => {
    handleClose();
    setTimeout(() => {
      setActiveStep(1);
      setSelectedPatient(null);
      setAiForm({ service_status: "", reason: "", Remark: "", in_folloup: "" });
      setVisibleCount(20);
      setPrevFollowups([]); // 🌟 clear stale history on close
    }, 300);
  };

  return (
    <Modal
      open={open}
      onClose={closeAndReset}
      closeAfterTransition
      sx={{ bgcolor: "rgba(0, 0, 0, 0.5)" }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "95%", sm: 700 },
          maxHeight: "85vh",
          bgcolor: "#f8fafc",
          borderRadius: "16px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: 24,
          animation: open ? `${modalPop} 0.3s ease-out forwards` : "none",
        }}
      >
        {/* ================= HEADER ================= */}
        <Box
          sx={{
            background: "linear-gradient(90deg, #1e1b4b 0%, #3b0764 100%)",
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "3px solid #00e5ff",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              color: "#fff",
            }}
          >
            {activeStep === 2 ? (
              <IconButton
                size="small"
                onClick={() => setActiveStep(1)}
                sx={{
                  color: "#00e5ff",
                  "&:hover": { bgcolor: "rgba(0, 229, 255, 0.1)" },
                }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            ) : (
              <PsychologyIcon sx={{ color: "#00e5ff", fontSize: 28 }} />
            )}
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, letterSpacing: "0.5px" }}
            >
              {activeStep === 1
                ? "AI Patient Service Drop-off Predictions"
                : "Submit Reason"}
            </Typography>
          </Box>
          <IconButton
            onClick={closeAndReset}
            sx={{
              color: "rgba(255,255,255,0.7)",
              "&:hover": { color: "#fff" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* ================= BODY ================= */}
        <Box
          onScroll={handleScroll}
          sx={{
            p: 3,
            overflowY: "auto",
            flexGrow: 1,
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#c084fc",
              borderRadius: "10px",
            },
          }}
        >
          {/* STEP 1: Date Picker & Patient List */}
          {activeStep === 1 && (
            <Box>
              <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Grid item xs={8}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="Select Date"
                    value={selectedDate}
                    onChange={handleDateChange}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        bgcolor: "#fff",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Chip
                    icon={<SensorsIcon sx={{ fontSize: "16px" }} />}
                    label={
                      loading
                        ? "Analyzing..."
                        : `${Array.isArray(predictions) ? predictions.length : 0} Found`
                    }
                    sx={{
                      width: "100%",
                      fontWeight: 600,
                      borderRadius: "8px",
                      bgcolor: loading
                        ? "rgba(168,85,247,0.1)"
                        : "rgba(0, 229, 255, 0.1)",
                      color: loading ? "#a855f7" : "#00838f",
                      border: `1px solid ${loading ? "#a855f7" : "#00e5ff"}`,
                    }}
                  />
                </Grid>
              </Grid>

              {/* AI Loader */}
              {loading ? (
                <Box
                  sx={{
                    position: "relative",
                    height: "180px",
                    mt: 2,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "12px",
                    bgcolor: "#fff",
                    border: "1px solid #e2e8f0",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      width: "100%",
                      height: "3px",
                      background:
                        "linear-gradient(90deg, transparent, #00e5ff, transparent)",
                      animation: `${aiScanLine} 1.5s infinite linear`,
                    }}
                  />
                  <AutoAwesomeIcon
                    sx={{ fontSize: 40, color: "#a855f7", mb: 2, opacity: 0.8 }}
                  />
                  <Typography sx={{ fontWeight: 600, color: "#4f46e5" }}>
                    {loadingText}
                  </Typography>
                </Box>
              ) : !Array.isArray(predictions) || predictions.length === 0 ? (
                <Box sx={{ textAlign: "center", mt: 6, mb: 4, opacity: 0.7 }}>
                  <PersonRemoveIcon
                    sx={{ fontSize: 50, color: "#9ca3af", mb: 1 }}
                  />
                  <Typography variant="body1" sx={{ color: "#6b7280" }}>
                    No predictions found for this date.
                  </Typography>
                </Box>
              ) : (
                <List sx={{ p: 0, animation: `${fadeIn} 0.4s ease` }}>
                  {predictions.slice(0, visibleCount).map((patient, index) => (
                    <Paper
                      key={index}
                      elevation={0}
                      sx={{
                        mb: 2,
                        p: 2,
                        borderRadius: "8px",
                        bgcolor: "#fff",
                        border: "1px solid #e2e8f0",
                        borderLeft: "4px solid #a855f7",
                        "&:hover": {
                          borderColor: "#c084fc",
                          bgcolor: "#faf5ff",
                        },
                        transition: "all 0.2s ease",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                        }}
                      >
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 700, color: "#1e293b", mb: 0.5 }}
                          >
                            {patient["Patient Name"] ||
                              `Patient ID: ${patient.patient_id}`}
                          </Typography>

                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flexWrap: "wrap",
                              gap: 2,
                              mb: 1.5,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#64748b" }}
                            >
                              Session ID: {patient.last_session_id_id}
                            </Typography>

                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <PhoneIcon
                                sx={{ fontSize: 14, mr: 0.5, color: "#10b981" }}
                              />
                              <Typography
                                variant="caption"
                                sx={{ color: "#64748b", fontWeight: 500 }}
                              >
                                {patient.Patient_Mobile_No || "N/A"}
                              </Typography>
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <CalendarMonthIcon
                                sx={{ fontSize: 14, mr: 0.5, color: "#8b5cf6" }}
                              />
                              <Typography
                                variant="caption"
                                sx={{ color: "#64748b", fontWeight: 500 }}
                              >
                                {patient.Last_Service_Date || "N/A"}
                              </Typography>
                            </Box>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 2.5,
                              rowGap: 1.5,
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <StarRateIcon
                                sx={{ fontSize: 18, mr: 0.3, color: "#f59e0b" }}
                              />
                              <Typography
                                variant="body2"
                                sx={{ color: "#334155", fontWeight: 500 }}
                              >
                                {patient["Feedback Rating"]
                                  ? `${patient["Feedback Rating"]}/5`
                                  : "N/A"}
                              </Typography>
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <AccessTimeIcon
                                sx={{ fontSize: 16, mr: 0.5, color: "#ef4444" }}
                              />
                              <Typography
                                variant="body2"
                                sx={{ color: "#334155", fontWeight: 500 }}
                              >
                                {patient.Days_Since_Last_Visit} Days Ago
                              </Typography>
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <MedicalServicesIcon
                                sx={{ fontSize: 16, mr: 0.5, color: "#3b82f6" }}
                              />
                              <Typography
                                variant="body2"
                                sx={{ color: "#334155", fontWeight: 500 }}
                              >
                                {patient.Last_Treatment_Type || "N/A"}
                              </Typography>
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <TagIcon
                                sx={{ fontSize: 16, mr: 0.3, color: "#6366f1" }}
                              />
                              <Typography
                                variant="body2"
                                sx={{ color: "#334155", fontWeight: 500 }}
                              >
                                Total Visits: {patient.Total_Visits || 0}
                              </Typography>
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <CreditCardIcon
                                sx={{
                                  fontSize: 16,
                                  mr: 0.5,
                                  color:
                                    patient.Last_Visit_Payment === "Delayed"
                                      ? "#ef4444"
                                      : "#10b981",
                                }}
                              />
                              <Typography
                                variant="body2"
                                sx={{
                                  color:
                                    patient.Last_Visit_Payment === "Delayed"
                                      ? "#ef4444"
                                      : "#10b981",
                                  fontWeight: 600,
                                }}
                              >
                                Pay: {patient.Last_Visit_Payment || "N/A"}
                              </Typography>
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <WarningAmberIcon
                                sx={{
                                  fontSize: 18,
                                  mr: 0.3,
                                  color:
                                    patient.No_Visit_30_Days_Label === "Yes"
                                      ? "#f97316"
                                      : "#64748b",
                                }}
                              />
                              <Typography
                                variant="body2"
                                sx={{
                                  color:
                                    patient.No_Visit_30_Days_Label === "Yes"
                                      ? "#f97316"
                                      : "#64748b",
                                  fontWeight: 500,
                                }}
                              >
                                {"> 30 Days:"}{" "}
                                {patient.No_Visit_30_Days_Label || "N/A"}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        {/* Action Button */}
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleActionClick(patient)}
                          sx={{
                            textTransform: "none",
                            color: "#6366f1",
                            borderColor: "#6366f1",
                            borderRadius: "6px",
                            fontWeight: 600,
                            minWidth: "110px",
                            mt: 0.5,
                            "&:hover": { bgcolor: "#6366f1", color: "#fff" },
                          }}
                        >
                          Add Follow-up
                        </Button>
                      </Box>
                    </Paper>
                  ))}

                  {visibleCount < predictions.length && (
                    <Box sx={{ textAlign: "center", py: 2 }}>
                      <Typography variant="body2" sx={{ color: "#9ca3af" }}>
                        Scroll down to load more...
                      </Typography>
                    </Box>
                  )}
                </List>
              )}
            </Box>
          )}

          {/* STEP 2: Standard Form View */}
          {activeStep === 2 && selectedPatient && (
            <Box sx={{ animation: `${fadeIn} 0.3s ease` }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 3,
                  bgcolor: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  borderRadius: "8px",
                }}
              >
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, color: "#166534" }}
                    >
                      {selectedPatient["Patient Name"]}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#15803d",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <PhoneIcon sx={{ fontSize: 16, mr: 0.5 }} />{" "}
                      {selectedPatient.Patient_Mobile_No}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#15803d",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <AccessTimeIcon sx={{ fontSize: 16, mr: 0.5 }} />{" "}
                      {selectedPatient.Days_Since_Last_Visit} days ago
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* 🌟 Previous Follow-up History (pre drop-off) */}
              {loadingHistory ? (
                <Box
                  sx={{ display: "flex", alignItems: "center", mb: 3, gap: 1 }}
                >
                  <HistoryIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                  <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                    Loading previous follow-up history...
                  </Typography>
                </Box>
              ) : prevFollowups.length > 0 ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 3,
                    bgcolor: "#fff7ed",
                    border: "1px solid #fed7aa",
                    borderRadius: "8px",
                    animation: `${fadeIn} 0.3s ease`,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <HistoryIcon sx={{ fontSize: 18, color: "#9a3412" }} />
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, color: "#9a3412" }}
                    >
                      Previous Follow-up History
                    </Typography>
                  </Box>

                  {prevFollowups.map((f, idx) => (
                    <Box
                      key={f.plsds_id ?? idx}
                      sx={{
                        mb: idx === prevFollowups.length - 1 ? 0 : 1.5,
                        pb: idx === prevFollowups.length - 1 ? 0 : 1.5,
                        borderBottom:
                          idx === prevFollowups.length - 1
                            ? "none"
                            : "1px dashed #fed7aa",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: "#78350f", fontWeight: 600 }}
                      >
                        Status:{" "}
                        {SERVICE_STATUS_LABELS[f.service_status] ||
                          f.service_status}{" "}
                        {f.reason_title ? `— ${f.reason_title}` : ""}
                      </Typography>
                      {f.in_folloup && (
                        <Typography
                          variant="caption"
                          sx={{ color: "#a16207", display: "block" }}
                        >
                          Follow-up scheduled:{" "}
                          {new Date(f.in_folloup).toLocaleString()}
                        </Typography>
                      )}
                      {f.Remark && (
                        <Typography variant="body2" sx={{ color: "#78350f" }}>
                          Remark: {f.Remark}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Paper>
              ) : null}

              <form onSubmit={handleAiSubmit}>
                <Grid container spacing={2}>
                  {/* Service Status Select (Always Show) */}
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small" required>
                      <InputLabel>Service Status</InputLabel>
                      <Select
                        name="service_status"
                        value={aiForm.service_status}
                        onChange={handleFormChange}
                        label="Service Status"
                        sx={{ bgcolor: "#fff", borderRadius: "8px" }}
                      >
                        <MenuItem value={1}>
                          1 - Converted Into Service
                        </MenuItem>
                        <MenuItem value={2}>2 - In Follow-up</MenuItem>
                        <MenuItem value={3}>3 - Dropped</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* 🌟 In-Followup Only (Status === 2) */}
                  {parseInt(aiForm.service_status) === 2 && (
                    <Grid
                      item
                      xs={12}
                      sx={{ animation: `${fadeIn} 0.2s ease` }}
                    >
                      <TextField
                        fullWidth
                        size="small"
                        required
                        type="datetime-local"
                        label="Follow-up Date & Time"
                        name="in_folloup"
                        value={aiForm.in_folloup}
                        onChange={handleFormChange}
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            bgcolor: "#fff",
                            borderRadius: "8px",
                          },
                        }}
                      />
                    </Grid>
                  )}

                  {/* 🌟 Drop-off Reason Only (Status === 3) */}
                  {parseInt(aiForm.service_status) === 3 && (
                    <Grid
                      item
                      xs={12}
                      sx={{ animation: `${fadeIn} 0.2s ease` }}
                    >
                      <FormControl fullWidth size="small" required>
                        <InputLabel>Drop-off Reason</InputLabel>
                        <Select
                          name="reason"
                          value={aiForm.reason}
                          onChange={handleFormChange}
                          label="Drop-off Reason"
                          sx={{ bgcolor: "#fff", borderRadius: "8px" }}
                        >
                          {Array.isArray(aiReasons) &&
                            aiReasons.map((r) => (
                              <MenuItem key={r.ptdr_id} value={r.ptdr_id}>
                                {r.reason}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}

                  {/* Remark Field (Always Show) */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      required
                      label="Additional Remark"
                      name="Remark"
                      multiline
                      rows={3}
                      value={aiForm.Remark}
                      onChange={handleFormChange}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          bgcolor: "#fff",
                          borderRadius: "8px",
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sx={{ mt: 1 }}>
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      endIcon={<SendIcon />}
                      sx={{
                        background:
                          "linear-gradient(90deg, #3b0764 0%, #1e1b4b 100%)",
                        color: "#00e5ff",
                        textTransform: "none",
                        fontWeight: 600,
                        py: 1.2,
                        borderRadius: "8px",
                        boxShadow: "none",
                        "&:hover": {
                          background: "#00e5ff",
                          color: "#1e1b4b",
                          boxShadow: "none",
                        },
                        transition: "all 0.2s",
                      }}
                    >
                      {parseInt(aiForm.service_status) === 1
                        ? "Submit & Go To Add Service"
                        : "Submit Follow-up Reason"}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default AIDropoffModal;
